import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  Snackbar,
  Alert
} from '@mui/material';
import VideoInputForm from '../components/VideoInputForm';
import VideoMetadataCard from '../components/VideoMetadataCard';
import OutputFormatSelectorCompact from '../components/OutputFormatSelectorCompact';
import ContentDisplay from '../components/ContentDisplay';
import LoginRequiredDialog from '../components/LoginRequiredDialog';
import { OutputFormat } from '../components/OutputFormatSelector';
import { 
  ContentCategory, 
  OutputLanguage,
  VideoLanguage,
  generateFullCapsule,
  generateKeyInsights,
  generateMindMap,
  generateMCQs,
  generateFAQs,
  generateFlashCards,
  generateStudyNotes,
  generateTerminology,
  generateChatResponse
} from '../services/contentService';
import { getVideoInfo, TranscriptData } from '../services/videoService';
import { useAuth } from '../contexts/AuthContext';

const MainPage: React.FC = () => {
  // Auth context
  const { isAuthenticated, userSubscription } = useAuth();
  
  // State for video info
  const [videoId, setVideoId] = useState<string>('');
  const [videoTitle, setVideoTitle] = useState<string>('');
  const [videoDuration, setVideoDuration] = useState<string>('');
  const [videoThumbnail, setVideoThumbnail] = useState<string>('');
  const [videoTranscript, setVideoTranscript] = useState<TranscriptData[]>([]);
  
  // State for content generation
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('fullCapsule');
  const [outputLanguage, setOutputLanguage] = useState<OutputLanguage>(OutputLanguage.English);
  const [contentCategory, setContentCategory] = useState<ContentCategory>(ContentCategory.General);
  
  // State for generated content
  const [generatedContent, setGeneratedContent] = useState<any>(null);
  
  // State for loading and errors
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // State for login required dialog
  const [loginDialogOpen, setLoginDialogOpen] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pendingAction, setPendingAction] = useState<{
    type: 'url' | 'upload';
    url?: string;
    file?: File;
    videoLanguage: VideoLanguage;
    outputLanguage: OutputLanguage;
    category?: ContentCategory;
  } | null>(null);
  
  // State for notifications
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Helper function to calculate time saved based on video duration
  const calculateTimeSaved = (duration: string, readingTimeMinutes: number = 0, savingPercentage: number = 0.7): number => {
    // Convert duration string to minutes
    const durationParts = duration.split(':');
    let durationMinutes = 0;
    
    if (durationParts.length === 3) {
      // Format: H:MM:SS
      durationMinutes = 
        parseInt(durationParts[0]) * 60 + 
        parseInt(durationParts[1]) + 
        Math.ceil(parseInt(durationParts[2]) / 60);
    } else if (durationParts.length === 2) {
      // Format: M:SS
      durationMinutes = 
        parseInt(durationParts[0]) + 
        Math.ceil(parseInt(durationParts[1]) / 60);
    }
    
    // Calculate time saved
    if (readingTimeMinutes > 0) {
      // If reading time is provided, calculate actual time saved
      return Math.max(
        Math.round(durationMinutes * savingPercentage), // At least savingPercentage of video time is saved
        durationMinutes - readingTimeMinutes, // Or the actual difference
        1 // At minimum 1 minute saved
      );
    } else {
      // Otherwise just use the percentage
      return Math.max(
        Math.round(durationMinutes * savingPercentage),
        1 // At minimum 1 minute saved
      );
    }
  };
  
  // Handle login dialog close
  const handleLoginDialogClose = () => {
    setLoginDialogOpen(false);
    setPendingAction(null);
  };
  
  // Handle video file upload
  const handleVideoUpload = async (
    file: File,
    videoLanguage: VideoLanguage,
    outputLanguage: OutputLanguage,
    category?: ContentCategory
  ) => {
    console.log('handleVideoUpload called');
    console.log('File:', file.name, file.size, file.type);
    console.log('Video Language:', videoLanguage);
    console.log('Output Language:', outputLanguage);
    console.log('Category:', category);
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      // Store the pending action
      setPendingAction({
        type: 'upload',
        file,
        videoLanguage,
        outputLanguage,
        category
      });
      
      // Show login dialog
      setLoginDialogOpen(true);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setGeneratedContent(null);
    
    try {
      // Update category and language if provided
      if (category) {
        setContentCategory(category);
      }
      
      // Update output language
      setOutputLanguage(outputLanguage);
      
      // For demo purposes, we'll just use a mock video ID and info
      const mockVideoId = 'uploaded_' + Date.now();
      setVideoId(mockVideoId);
      setVideoTitle(file.name);
      setVideoDuration('10:00'); // Mock duration
      setVideoThumbnail(''); // No thumbnail for uploaded videos
      setVideoTranscript([]); // Empty transcript for now
      
      // Show success notification
      showNotification('Video uploaded successfully', 'success');
      
      // Generate mock content
      await generateContent(
        mockVideoId,
        file.name,
        '10:00',
        [],
        outputFormat,
        outputLanguage,
        category || contentCategory
      );
    } catch (err) {
      console.error('Error uploading video:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      showNotification('Error uploading video', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle video URL submission
  const handleVideoSubmit = async (
    url: string, 
    videoLanguage: VideoLanguage, 
    outputLanguage: OutputLanguage, 
    category?: ContentCategory
  ) => {
    console.log('handleVideoSubmit called');
    console.log('URL:', url);
    console.log('Video Language:', videoLanguage);
    console.log('Output Language:', outputLanguage);
    console.log('Category:', category);
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      // Store the pending action
      setPendingAction({
        type: 'url',
        url,
        videoLanguage,
        outputLanguage,
        category
      });
      
      // Show login dialog
      setLoginDialogOpen(true);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setGeneratedContent(null);
    
    try {
      // Update category and language if provided
      if (category) {
        setContentCategory(category);
      }
      
      // Update output language
      setOutputLanguage(outputLanguage);
      
      // Extract video ID from URL
      const videoIdMatch = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
      if (!videoIdMatch) {
        throw new Error('Invalid YouTube URL');
      }
      
      const id = videoIdMatch[1];
      setVideoId(id);
      
      // Get video info
      const videoInfo = await getVideoInfo(id);
      setVideoTitle(videoInfo.title);
      setVideoDuration(videoInfo.duration);
      setVideoThumbnail(videoInfo.thumbnailUrl);
      setVideoTranscript(videoInfo.transcript);
      
      // Show success notification
      showNotification('Video loaded successfully', 'success');
      
      // Generate content based on selected format
      await generateContent(
        id, 
        videoInfo.title, 
        videoInfo.duration, 
        videoInfo.transcript, 
        outputFormat, 
        outputLanguage, 
        category || contentCategory
      );
    } catch (err) {
      console.error('Error loading video:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      showNotification('Error loading video', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle output format change
  const handleOutputFormatChange = async (format: OutputFormat) => {
    if (videoId && videoTitle && videoDuration && videoTranscript.length > 0) {
      setOutputFormat(format);
      setIsLoading(true);
      setError(null);
      
      try {
        await generateContent(videoId, videoTitle, videoDuration, videoTranscript, format);
      } catch (err) {
        console.error('Error generating content:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        showNotification('Error generating content', 'error');
      } finally {
        setIsLoading(false);
      }
    } else {
      setOutputFormat(format);
    }
  };
  
  // Generate content based on selected format
  const generateContent = async (
    id: string,
    title: string,
    duration: string,
    transcript: TranscriptData[],
    format: OutputFormat = outputFormat,
    language: OutputLanguage = outputLanguage,
    category: ContentCategory = contentCategory
  ): Promise<void> => {
    try {
      console.log(`Generating content for format: ${format}, language: ${language}, category: ${category}`);
      
      let content;
      const videoUrl = `https://www.youtube.com/watch?v=${id}`;
      
      // Always use real API, NEVER use mock data
      // This code path should never be executed, but keeping it for development purposes only
      if (false) {
        console.log('Using mock data for content generation');
        
        // Generate mock content based on format
        switch (format) {
          case 'fullCapsule':
            // Generate a comprehensive list of at least 50 points for the mock data
            const mockPoints = [];
            for (let i = 1; i <= 50; i++) {
              mockPoints.push(`${i}. This is point ${i} about the video "${title}". Each point covers a specific detail from the video to ensure comprehensive coverage.`);
            }
            
            const mockContent = `# ${title}\n\n## Comprehensive Breakdown\n\n${mockPoints.join('\n\n')}`;
            
            // Calculate a realistic time saved value
            const mockDurationParts = duration.split(':');
            let mockDurationMinutes = 10; // Default to 10 minutes
            
            if (mockDurationParts.length === 3) {
              mockDurationMinutes =
                parseInt(mockDurationParts[0]) * 60 +
                parseInt(mockDurationParts[1]);
            } else if (mockDurationParts.length === 2) {
              mockDurationMinutes = parseInt(mockDurationParts[0]);
            }
            
            const mockTimeSaved = Math.max(Math.round(mockDurationMinutes * 0.7), 5);
            
            content = {
              content: mockContent,
              timeSaved: mockTimeSaved
            };
            break;
          case 'keyInsights':
            content = {
              timeSaved: 15,
              insights: [
                {
                  title: 'Main Concept Explained',
                  content: 'The video explains the fundamental principles of the topic, focusing on key aspects that are essential for understanding the subject matter.'
                },
                {
                  title: 'Practical Applications',
                  content: 'Several real-world applications are demonstrated, showing how the concepts can be applied in various scenarios.'
                },
                {
                  title: 'Common Misconceptions',
                  content: 'The presenter addresses several common misconceptions about the topic, clarifying points that are often misunderstood.'
                }
              ]
            };
            break;
          case 'mindMap':
            content = {
              name: title,
              children: [
                {
                  name: 'Fundamental Principles',
                  children: [
                    { name: 'Core Concept 1', children: [] },
                    { name: 'Core Concept 2', children: [] },
                    { name: 'Core Concept 3', children: [] }
                  ]
                },
                {
                  name: 'Practical Applications',
                  children: [
                    { name: 'Application 1', children: [] },
                    { name: 'Application 2', children: [] },
                    { name: 'Application 3', children: [] }
                  ]
                },
                {
                  name: 'Common Misconceptions',
                  children: [
                    { name: 'Misconception 1', children: [] },
                    { name: 'Misconception 2', children: [] }
                  ]
                }
              ]
            };
            break;
          case 'mcqs':
            content = [
              {
                question: 'What is the main focus of the video?',
                options: [
                  'Entertainment',
                  'Educational content',
                  'Product review',
                  'News report'
                ],
                correctAnswer: 1,
                explanation: 'The video primarily focuses on educational content, explaining concepts and their applications.'
              },
              {
                question: 'Which of the following is NOT a fundamental principle covered in the video?',
                options: [
                  'Core Concept 1',
                  'Core Concept 2',
                  'Core Concept 3',
                  'Advanced Theory X'
                ],
                correctAnswer: 3,
                explanation: 'Advanced Theory X is not mentioned as one of the fundamental principles in the video.'
              }
            ];
            break;
          case 'faqs':
            content = [
              {
                question: 'What are the fundamental principles covered in the video?',
                answer: 'The video covers several fundamental principles including core concepts that form the foundation of the subject matter.'
              },
              {
                question: 'How can these concepts be applied in real-world scenarios?',
                answer: 'The video demonstrates several practical applications, showing how the concepts can be used to solve real-world problems.'
              }
            ];
            break;
          case 'flashCards':
            content = [
              {
                front: 'Core Concept 1',
                back: 'Explanation of Core Concept 1 and its importance to the overall topic.'
              },
              {
                front: 'Core Concept 2',
                back: 'Explanation of Core Concept 2 and its relationship to other concepts.'
              }
            ];
            break;
          case 'studyNotes':
            content = {
              timeSaved: 20,
              fullNotes: "# Study Notes\n\nThese are comprehensive study notes based on the video content.\n\n## Main Topic\n\nThe main topic covers essential concepts and principles.\n\n## Key Points\n\n- Point 1: Important information about the subject\n- Point 2: Critical aspects to understand\n- Point 3: Practical applications of the concept\n\n## Summary\n\nIn summary, the video provides valuable insights into the topic.",
              summary: "The video covers important concepts and provides practical examples of their application.",
              keyPoints: [
                "The first key point explains the fundamental principle",
                "The second key point demonstrates practical applications",
                "The third key point addresses common misconceptions"
              ],
              tables: [
                {
                  title: "Comparison of Approaches",
                  content: "| Approach | Advantages | Disadvantages |\n|----------|------------|---------------|\n| Method 1 | Fast, efficient | Limited scope |\n| Method 2 | Comprehensive | Time-consuming |"
                }
              ]
            };
            break;
          case 'terminology':
            content = [
              {
                term: 'Core Concept 1',
                definition: 'Definition of Core Concept 1',
                context: 'Used in the context of fundamental principles.'
              },
              {
                term: 'Core Concept 2',
                definition: 'Definition of Core Concept 2',
                context: 'Used in the context of fundamental principles.'
              }
            ];
            break;
          case 'aiChat':
            // For AI Chat, we don't need to generate content upfront
            content = null;
            break;
          default:
            throw new Error('Unknown output format');
        }
      } else {
        // Use real API
        try {
          switch (format) {
            case 'fullCapsule':
              const fullCapsuleContent = await generateFullCapsule(videoUrl, language, category);
              
              // Calculate time saved based on video duration and content length
              // Estimate reading time based on word count (200 words per minute)
              const wordCount = fullCapsuleContent.split(/\s+/).length;
              const readingTimeMinutes = Math.ceil(wordCount / 200);
              
              // Calculate time saved using our helper function
              const timeSaved = calculateTimeSaved(duration, readingTimeMinutes, 0.7);
              
              content = {
                content: fullCapsuleContent,
                timeSaved: timeSaved
              };
              break;
            case 'keyInsights':
              const keyInsights = await generateKeyInsights(videoUrl, language, category);
              
              // Calculate time saved for key insights (typically saves more time than full capsule)
              const keyInsightsTimeSaved = calculateTimeSaved(duration, 0, 0.75);
              
              content = {
                timeSaved: keyInsightsTimeSaved,
                insights: keyInsights.map((insight, index) => ({
                  title: `Key Insight ${index + 1}`,
                  content: insight
                }))
              };
              break;
            case 'mindMap':
              content = await generateMindMap(videoUrl, language, category);
              break;
            case 'mcqs':
              content = await generateMCQs(videoUrl, language, category);
              break;
            case 'faqs':
              content = await generateFAQs(videoUrl, language, category);
              break;
            case 'flashCards':
              content = await generateFlashCards(videoUrl, language, category);
              break;
            case 'studyNotes':
              const studyNotes = await generateStudyNotes(videoUrl, language, category);
              
              // Calculate time saved for study notes
              const studyNotesTimeSaved = calculateTimeSaved(duration, 0, 0.7);
              
              content = {
                timeSaved: studyNotesTimeSaved,
                fullNotes: studyNotes
              };
              break;
            case 'terminology':
              content = await generateTerminology(videoUrl, language, category);
              break;
            case 'aiChat':
              // For AI Chat, we don't need to generate content upfront
              content = null;
              break;
            default:
              throw new Error('Unknown output format');
          }
        } catch (apiError) {
          console.error('API error, retrying with increased timeout:', apiError);
          
          // Check if it's a timeout error or JSON parsing error
          const errorMessage = apiError instanceof Error ? apiError.message : String(apiError);
          const isTimeout = errorMessage.includes('timeout') || errorMessage.includes('ECONNABORTED');
          const isJsonError = errorMessage.includes('JSON') || errorMessage.includes('Syntax error');
          
          if (isTimeout) {
            showNotification('API timeout, retrying with increased timeout...', 'info');
          } else if (isJsonError) {
            showNotification('JSON parsing error, retrying with improved parsing...', 'info');
          } else {
            showNotification('API error, retrying...', 'info');
          }
          
          // Wait longer before retrying (5 seconds)
          await new Promise(resolve => setTimeout(resolve, 5000));
          
          // Simplified retry approach - just retry the same function with the same parameters
          try {
            // Retry the API call
            let retryContent;
            
            // For timeout errors, we'll try with a simpler prompt by using fullCapsule format
            // regardless of the original format if this is the second retry
            if (isTimeout && format !== 'fullCapsule') {
              showNotification('Trying with simplified format...', 'info');
              
              // Get the full capsule content which might be more reliable
              const simplifiedContent = await generateFullCapsule(videoUrl, language, category);
              const wordCount = simplifiedContent.split(/\s+/).length;
              const readingTimeMinutes = Math.ceil(wordCount / 200);
              const timeSaved = calculateTimeSaved(duration, readingTimeMinutes, 0.7);
              
              content = {
                content: simplifiedContent,
                timeSaved: timeSaved
              };
            } else {
              // Standard retry with the same format
              switch (format) {
                case 'fullCapsule':
                  const retryFullCapsuleContent = await generateFullCapsule(videoUrl, language, category);
                  const retryWordCount = retryFullCapsuleContent.split(/\s+/).length;
                  const retryReadingTimeMinutes = Math.ceil(retryWordCount / 200);
                  const retryTimeSaved = calculateTimeSaved(duration, retryReadingTimeMinutes, 0.7);
                  
                  retryContent = {
                    content: retryFullCapsuleContent,
                    timeSaved: retryTimeSaved
                  };
                  break;
                case 'keyInsights':
                  const retryKeyInsights = await generateKeyInsights(videoUrl, language, category);
                  const retryKeyInsightsTimeSaved = calculateTimeSaved(duration, 0, 0.75);
                  
                  retryContent = {
                    timeSaved: retryKeyInsightsTimeSaved,
                    insights: retryKeyInsights.map((insight, index) => ({
                      title: `Key Insight ${index + 1}`,
                      content: insight
                    }))
                  };
                  break;
                case 'mindMap':
                  retryContent = await generateMindMap(videoUrl, language, category);
                  break;
                case 'mcqs':
                  retryContent = await generateMCQs(videoUrl, language, category);
                  break;
                case 'faqs':
                  retryContent = await generateFAQs(videoUrl, language, category);
                  break;
                case 'flashCards':
                  retryContent = await generateFlashCards(videoUrl, language, category);
                  break;
                case 'studyNotes':
                  const retryStudyNotes = await generateStudyNotes(videoUrl, language, category);
                  const retryStudyNotesTimeSaved = calculateTimeSaved(duration, 0, 0.7);
                  
                  retryContent = {
                    timeSaved: retryStudyNotesTimeSaved,
                    fullNotes: retryStudyNotes
                  };
                  break;
                case 'terminology':
                  retryContent = await generateTerminology(videoUrl, language, category);
                  break;
                default:
                  throw new Error('Format not supported for retry');
              }
              
              content = retryContent;
            }
            
            showNotification('Content generated successfully on retry', 'success');
          } catch (retryError) {
            console.error('Retry also failed:', retryError);
            showNotification('Second attempt failed, trying final approach...', 'info');
            
            // Wait even longer before final retry (8 seconds)
            await new Promise(resolve => setTimeout(resolve, 8000));
            
            // Final attempt - always use fullCapsule as it's the most reliable
            try {
              const finalAttemptContent = await generateFullCapsule(videoUrl, language, category);
              const finalWordCount = finalAttemptContent.split(/\s+/).length;
              const finalReadingTimeMinutes = Math.ceil(finalWordCount / 200);
              const finalTimeSaved = calculateTimeSaved(duration, finalReadingTimeMinutes, 0.7);
              
              content = {
                content: finalAttemptContent,
                timeSaved: finalTimeSaved
              };
              
              showNotification('Content generated successfully on final attempt!', 'success');
            } catch (finalError) {
              console.error('All attempts failed:', finalError);
              showNotification('Unable to generate content after multiple attempts. Please try again later or try a different video.', 'error');
              throw new Error('Failed to generate content after multiple attempts: ' + (finalError instanceof Error ? finalError.message : String(finalError)));
            }
          }
        }
      }
      
      console.log('Generated content:', content);
      
      // Add a delay to show the loading UI
      // In a real implementation, this delay would be replaced by the actual API call time
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      setGeneratedContent(content);
    } catch (err) {
      console.error('Error generating content:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      showNotification('Error generating content', 'error');
    }
  };
  
  // Handle chat question
  const handleChatQuestion = async (question: string) => {
    try {
      const videoUrl = `https://www.youtube.com/watch?v=${videoId}`;
      const response = await generateChatResponse(
        videoUrl,
        question,
        outputLanguage,
        contentCategory
      );
      
      // In a real implementation, you would update the chat messages state
      console.log('Chat response:', response);
      
      return response;
    } catch (err) {
      console.error('Error generating chat response:', err);
      throw err;
    }
  };
  
  // Handle content actions
  const handleContentSave = () => {
    // In a real implementation, you would save the content to the user's library
    showNotification('Content saved to your library', 'success');
  };
  
  const handleContentCopy = () => {
    showNotification('Content copied to clipboard', 'success');
  };
  
  const handleContentShare = () => {
    showNotification('Share dialog opened', 'info');
  };
  
  const handleContentDownload = () => {
    showNotification('Content downloaded', 'success');
  };
  
  const handleContentPrint = () => {
    showNotification('Print dialog opened', 'info');
  };
  
  // Show notification
  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };
  
  // Handle notification close
  const handleNotificationClose = () => {
    setNotification({
      ...notification,
      open: false
    });
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom>
          SharkPanzer
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Transform YouTube Videos into Knowledge Capsules
        </Typography>
      </Box>
      
      {/* Video Input */}
      <VideoInputForm onSubmit={handleVideoSubmit} onUpload={handleVideoUpload} />
      
      {/* Video Metadata */}
      {videoId && (
        <VideoMetadataCard
          videoId={videoId}
          title={videoTitle}
          duration={videoDuration}
          thumbnailUrl={videoThumbnail}
        />
      )}
      
      {/* Main Content Area with Sidebar */}
      {videoId && (
        <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
          {/* Sidebar */}
          <Box 
            sx={{ 
              width: 280, 
              flexShrink: 0,
            }}
          >
            <Paper 
              elevation={0} 
              className="glass-container"
              sx={{ 
                p: 2,
                borderRadius: 2
              }}
            >
              <OutputFormatSelectorCompact
                selectedFormat={outputFormat}
                onFormatChange={handleOutputFormatChange}
                userSubscription={userSubscription}
              />
            </Paper>
          </Box>
          
          {/* Main Content */}
          <Box sx={{ flexGrow: 1 }}>
            <ContentDisplay
              outputFormat={outputFormat}
              videoId={videoId}
              videoTitle={videoTitle}
              videoDuration={videoDuration}
              category={contentCategory}
              language={outputLanguage}
              isLoading={isLoading}
              error={error}
              content={generatedContent}
              isSaved={false}
              onSave={handleContentSave}
              onCopy={handleContentCopy}
              onShare={handleContentShare}
              onDownload={handleContentDownload}
              onPrint={handleContentPrint}
              onChatQuestion={handleChatQuestion}
            />
          </Box>
        </Box>
      )}
      
      {/* Login Required Dialog */}
      <LoginRequiredDialog
        open={loginDialogOpen}
        onClose={handleLoginDialogClose}
      />
      
      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleNotificationClose} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MainPage;
